import Immutable from 'immutable';
import dayjs from 'dayjs';
import {CANCELED, CLOSED, CLOSED_ARC, CLOSED_EXPIRED, CLOSED_SEQ, colorForState, OPEN} from './AlarmState';
import {AlarmHandler} from './AlarmHandler';
import Device from './Device';
import alarmTypes from './AlarmTypes';
import {QuestionIcon} from '../icons';
import {triggerIcons} from "./scenarios/triggerIcons";
import {eventToTrigger} from "./scenarios/eventToTrigger";
import {V4_ALARM_ORIGIN} from "./v4AlarmOrigin";
import {isAssistanceRequestAlarm} from "./v4AlarmSituation";

// Maps a raw `arc_forward` object to the list ring state; unknown status falls
// back to `forwarded_at` so it never spins forever. Null when not forwarded.
export function arcForwardStateOf(forward): 'forwarding' | 'success' | 'failed' | null {
    if (!forward) return null;
    const status = String(forward.status || '').toLowerCase();
    if (['failed', 'failure', 'error'].includes(status)) return 'failed';
    if (['success', 'succeeded', 'forwarded', 'delivered', 'ok', 'completed', 'done', 'sent'].includes(status)) return 'success';
    if (['forwarding', 'started', 'pending', 'processing', 'queued', 'sending', 'in_progress', ''].includes(status)) return 'forwarding';
    const forwardedAt = forward.forwarded_at;
    return forwardedAt && !String(forwardedAt).startsWith('0001-') ? 'success' : 'forwarding';
}

const AlarmModel = Immutable.Record({
    id: 0,
    date: 0,
    cancelable: false,
    checked: 0,
    notes: '',
    arc: null,
    state: 0,
    type: '',
    token: '',
    payload: {},
    snapshot: {},

    // V4: mirrors the ARC's `arc_forward.exclusive_closing` — when true only the ARC may close.
    exclusiveClosing: false,

    // V4: raw `arc_forward` object (or null); drives the list icon ring.
    arcForward: null,

    // V4: normalised `origin` snapshot (see utils/models/v4AlarmOrigin). Null for V3.
    origin: null,

    v4Type: '',

    // V4 assistance request: the alarm that caused it and the request it follows up ('' if none).
    sourceAlarmId: '',
    followUpOfId: '',

    // V4: the V3 alarm/device a V3 device forwarded it from (0 if none).
    v3AlarmId: 0,
    v3DeviceId: 0,

    // V4 RTC room. Its websocket_url is actor-specific and must be used verbatim.
    rtcRoom: null,

    accountName: '',
    deviceName: '',
    link: '',

    accountId: 0,
    deviceId: 0,

    disabled: false,
    wizard: {},

    v4: false,
    v4Data: null
});

export default class Alarm extends AlarmModel {
    id: number;
    cancelable: boolean;
    notes: string;
    arc: string;
    state: OPEN | CANCELED | CLOSED | CLOSED_ARC | CLOSED_SEQ | CLOSED_EXPIRED;
    type: string;
    disabled: boolean;
    token: string;
    snapshot: Object;

    deviceName: string;
    accountName: string;

    wizard: Object;

    canClose(device: Device): boolean {
        if (!device) return false;
        if (this.get('cancelable')) return false;
        // V4 ARC reserves exclusive closing (the `user.isARC` call-site exception still applies).
        if (this.get('exclusiveClosing')) return false;
        if (this.get('arc')) return false;
        return this.isOpen && !this.get('disabled');
    }

    get key() {
        return `alarm_${this.id}`;
    }

    get canOpen(): boolean {
        if (this.get('v4')) return false;
        return this.state === CLOSED;
    }

    get checkedDate(): dayjs {
        return dayjs.unix(this.get('checked'));
    }

    get color(): string {
        if (this.get('state') !== CANCELED && this.handledBy)
            return this.handledBy.color;
        return colorForState(this.get('state'));
    }

    get alarmDate(): dayjs {
        return dayjs.unix(this.date);
    }

    get handledBy(): AlarmHandler {
        if (this.state === CLOSED_EXPIRED)
            return AlarmHandler.Platform
        if (this.get('cancelable') || this.get('state') === CANCELED || this.get('state') === CLOSED_SEQ || this.cancelable)
            return AlarmHandler.SequriX;
        return null;
    }

    get handledByText(): string {
        if (this.state === OPEN) {
            return this.handledBy.handledByText;
        } else if (this.state === CANCELED) {
            return 'thisAlarmIsCancelled';
        }
        return this.handledBy.handledByTextPast;
    }

    get isCancelled(): boolean {
        return this.state === CANCELED;
    }

    get isOpen(): boolean {
        return this.state === OPEN;
    }

    get stateName(): string {
        switch (this.get('state')) {
            case OPEN:
                return 'open.alarm';
            case CANCELED:
                return 'cancelled';
            case CLOSED:
            case CLOSED_ARC:
            case CLOSED_SEQ:
                return 'closed';
            case CLOSED_EXPIRED:
                return 'expired';
            default:
                return 'unknownAlarmStatus';
        }
    }

    get isAssistanceRequest(): boolean {
        return isAssistanceRequestAlarm(this);
    }

    get typeName(): string {
        switch (this.get('type')) {
            case alarmTypes.SOS:
                return 'sosButton';
            case alarmTypes.LOW_PRIO_ALARM:
                return 'lowPriorityAlarm';
            case alarmTypes.MAN_DOWN:
                return 'manDown';
            case alarmTypes.RIPCORD:
                return 'ripcord';
            case alarmTypes.AMBER_SERVER:
                return 'timerNoConnection';
            case alarmTypes.AMBER:
                return 'timer';
            case alarmTypes.GENERIC_ALARM:
                return 'genericAlarm';
            default:
                return 'unknownAlarmType';
        }
    }

    get arcForwardState(): 'forwarding' | 'success' | 'failed' | null {
        return arcForwardStateOf(this.get('arcForward'));
    }

    // False when a user / API token / the platform raised it. Gated on `v4` so a V3
    // payload carrying its own `origin` can never flip it.
    get isDeviceRaised(): boolean {
        if (!this.get('v4')) return true;
        const origin = this.get('origin');
        if (!origin) return true;
        // The snapshot has to be there too, or callers wait on a device that never arrives.
        return origin.type === V4_ALARM_ORIGIN.DEVICE && !!origin.device;
    }

    // The i18n key naming what raised this alarm ('device', 'user', ...).
    get originLabelKey(): string {
        return (this.get('v4') && this.get('origin')?.labelKey) || 'device';
    }

    get trigger() {
        return eventToTrigger[this.get('type')];
    }

    get icon() {
        return triggerIcons[this.trigger] || QuestionIcon;
    }
}
